import type { UseQueryOptions } from "@tanstack/react-query";

import Sentry from "@similarweb/sentry-logger";

import { swSettings } from "app/common/services/swSettings";
import { getCampaignsConsumptionFromServer } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsCampaignUtils";
import type { ICampaignsConsumptionMetrics } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import {
    ALL_CAMPAIGNS_QUERY_PREFIX,
    RankTrackerQueryType,
} from "app/pages/digital-marketing/keywords-campaigns/shared/constants";
import { createCampaignQueryKey } from "app/pages/digital-marketing/keywords-campaigns/shared/utils";

import { useRankTrackerQuery } from "../useRankTrackerQuery";

type QueryOptions = Omit<UseQueryOptions<ICampaignsConsumptionMetrics>, "queryKey" | "queryFn">;

const getDefaultConsumptionMetrics = (): ICampaignsConsumptionMetrics => ({
    usedKeywords: 0,
    totalKeywords: swSettings.components.RankTracker.resources.NumberTrackedKeywords as number,
    usedCampaigns: 0,
    totalCampaigns: swSettings.components.RankTracker.resources.NumberOfCampaigns as number,
    hasMutualAssetsError: false,
});

export const useCampaignsConsumptionQuery = (options?: QueryOptions) => {
    return useRankTrackerQuery({
        queryKey: createCampaignQueryKey(ALL_CAMPAIGNS_QUERY_PREFIX, RankTrackerQueryType.CampaignsConsumption),
        queryFn: async () => {
            const defaultMetrics = getDefaultConsumptionMetrics();
            return getCampaignsConsumptionFromServer(defaultMetrics);
        },
        throwOnError: (error) => {
            Sentry.errorTracker({
                message: "Error fetching campaigns consumption metrics",
                level: "error",
                e: error,
            });
            return false;
        },
        ...options,
    });
};
